import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Share2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useData } from "../../context/DataContext";
import { ShareDialog } from "../reveal/ShareDialog";
import type { SharePayload } from "../../lib/share";
import { CountdownDots, SaysResult, StageHeader } from "../ua/Stage";
import type { Phase } from "../../hooks/useInstant";

interface Props {
  testId: string;
  title: string;
  stageClass: string;
  to: string;
  phase: Phase;
  stage: ReactNode;
  result?: string;
  detail?: string;
  idleHint: string;
  onGo: () => void;
  goLabel: string;
}

export function StagePanel({ testId, title, stageClass, to, phase, stage, result, detail, idleHint, onGo, goLabel }: Props) {
  const { isFavourite, toggleFavourite } = useData();
  const [share, setShare] = useState<SharePayload | null>(null);
  const entry = { kind: "tool", title, result: result || "", category: undefined };
  const fav = !!result && isFavourite(entry);
  return (
    <div className="flex flex-col overflow-hidden rounded-[22px] border-[3px] border-[#FFC15E] bg-[#FFF3D6] ua-tile" data-testid={testId}>
      <div className={`relative flex flex-col gap-2 px-3 pb-3 pt-3 ${stageClass}`}>
        <Link to={to} aria-label={`Open ${title}`} className="absolute left-3 top-3 rounded-full border border-white/40 p-1 text-white/80 hover:bg-white/10" data-testid={`${testId}-open-link`}>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
        <StageHeader title={title} />
        <button onClick={onGo} disabled={phase === "counting"} className="relative mx-auto flex h-44 w-full items-center justify-center sm:h-48" aria-label={goLabel} data-testid={`${testId}-stage-button`}>
          {stage}
        </button>
        <CountdownDots running={phase === "counting"} done={phase === "done"} duration={950} />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-3 py-3">
        {phase === "done" && result ? (
          <>
            <SaysResult result={result} detail={detail} testId={`${testId}-result`} />
            <div className="flex gap-2">
              <button className="ua-btn px-4 py-1.5 text-sm" onClick={onGo} data-testid={`${testId}-again-button`}>Again</button>
              <button className="rounded-full border-2 border-[#FFC15E] bg-white p-2" aria-label="Save" data-testid={`${testId}-save-button`}
                onClick={() => toast.success(toggleFavourite(entry) ? "Saved to favourites" : "Removed from favourites")}>
                <Heart className={`h-4 w-4 ${fav ? "fill-pop-pink text-pop-pink" : "text-[#6b3a12]"}`} strokeWidth={2.5} />
              </button>
              <button className="rounded-full border-2 border-[#FFC15E] bg-white p-2" aria-label="Share" data-testid={`${testId}-share-button`}
                onClick={() => setShare({ heading: title, result, context: detail })}>
                <Share2 className="h-4 w-4 text-[#6b3a12]" strokeWidth={2.5} />
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="ua-highlight text-lg">DITHER says…</span>
            <button className="ua-btn px-6 py-2 text-lg" onClick={onGo} disabled={phase === "counting"} data-testid={`${testId}-go-button`}>{phase === "counting" ? "…" : goLabel}</button>
            <p className="text-xs font-bold text-[#8a5a2b]">{idleHint}</p>
          </>
        )}
      </div>
      <ShareDialog payload={share} onClose={() => setShare(null)} />
    </div>
  );
}
